import React, { useRef } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Users, Check, Upload, FileText, X } from "lucide-react";
import { C, inr, longDate, ageLabel, AADHAAR_EXT, AADHAAR_MAX } from "./runShared";

export const inputCls =
  "w-full bg-white border border-[#E9E2D2] text-[#1A1A1A] rounded-lg px-3.5 py-3 text-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[#0D5C63] focus:ring-2 focus:ring-[#0D5C63]/15";

export function Field({ label, required, hint, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-[11px] tracking-[0.16em] uppercase font-semibold text-[#5B5B5B] mb-1.5">
        {label}{required && <span className="text-[#F26419]"> *</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-[#8a8478] mt-1">{hint}</span>}
    </label>
  );
}

export function Section({ n, title, sub, children, testid }) {
  return (
    <section className="run-rise bg-white border border-[#E9E2D2] rounded-2xl p-5 sm:p-7" data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="run-display text-3xl text-[#F26419] leading-none">{String(n).padStart(2, "0")}</span>
        <div>
          <h2 className="run-display text-xl sm:text-2xl text-[#08363A] leading-none uppercase">{title}</h2>
          {sub && <p className="text-xs text-[#6b655b] mt-1">{sub}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function RunCard({ run, selected, onSelect }) {
  const disabled = !run.is_open;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(run)}
      data-testid={`run-card-${run.id}`}
      className={`relative text-left rounded-2xl border-2 p-5 transition-[transform,border-color,background-color] duration-200 ${
        selected ? "border-[#F26419] bg-[#FFF7F0] -translate-y-0.5" : "border-[#E9E2D2] bg-white hover:border-[#0D5C63] hover:-translate-y-0.5"
      } ${disabled ? "opacity-55 cursor-not-allowed hover:translate-y-0" : ""}`}
    >
      {selected && (
        <span className="absolute top-4 right-4 h-7 w-7 rounded-full bg-[#F26419] text-white inline-flex items-center justify-center"><Check size={16} /></span>
      )}
      <div className="run-display text-6xl leading-none text-[#0D5C63]">{run.distance_km}<span className="text-2xl ml-1 text-[#F26419]">KM</span></div>
      <div className="mt-2 font-semibold text-[#1A1A1A]">{run.name}</div>
      <div className="mt-3 space-y-1.5 text-xs text-[#5B5B5B]">
        <div className="flex items-center gap-2"><CalendarDays size={13} />{longDate(run.event_date)}</div>
        {run.reporting_time && <div className="flex items-center gap-2"><Clock size={13} />Reporting {run.reporting_time}</div>}
        {run.venue && <div className="flex items-center gap-2"><MapPin size={13} />{run.venue}</div>}
        <div className="flex items-center gap-2"><Users size={13} />Age {ageLabel(run)}{run.slots_left != null && ` · ${run.slots_left} slots left`}</div>
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div className="run-display text-3xl text-[#08363A]" data-testid={`run-fee-${run.id}`}>{inr(run.fee)}</div>
        {disabled && <span className="text-[10px] uppercase tracking-wider font-bold text-[#B02020] bg-[#FDECEC] px-2 py-1 rounded-full">{run.closed_reason}</span>}
      </div>
    </button>
  );
}

export function AadhaarPicker({ file, onChange }) {
  const ref = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!AADHAAR_EXT.includes(ext)) return toast.error("Aadhaar: only PDF, JPG or PNG files are allowed");
    if (f.size === 0) return toast.error("Aadhaar: the file is empty (0 KB)");
    if (f.size > AADHAAR_MAX) return toast.error("Aadhaar: file must be 1 MB or smaller");
    onChange(f);
  };
  return (
    <div className="border-2 border-dashed border-[#E9E2D2] rounded-xl p-4 bg-[#FAFAF7]" data-testid="run-aadhaar-picker">
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} data-testid="run-aadhaar-input" />
      {file ? (
        <div className="flex items-center gap-3">
          <FileText size={22} style={{ color: C.teal }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="run-aadhaar-name">{file.name}</div>
            <div className="text-[11px] text-[#8a8478]">{Math.ceil(file.size / 1024)} KB</div>
          </div>
          <button type="button" onClick={() => onChange(null)} className="p-1.5 rounded-full hover:bg-[#FDECEC] text-[#B02020]" data-testid="run-aadhaar-remove"><X size={16} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} className="w-full flex flex-col items-center gap-1.5 py-3 text-[#0D5C63]" data-testid="run-aadhaar-btn">
          <Upload size={22} />
          <span className="text-sm font-semibold">Upload Aadhaar card</span>
          <span className="text-[11px] text-[#8a8478]">PDF, JPG or PNG · max 1 MB</span>
        </button>
      )}
    </div>
  );
}

const looksHtml = (v) => /<\/?[a-z][\s\S]*>/i.test(v || "");

export function RichHtml({ html, className = "", testid }) {
  if (!html) return null;
  return looksHtml(html)
    ? <div className={`prose prose-sm max-w-none ${className}`} data-testid={testid} dangerouslySetInnerHTML={{ __html: html }} />
    : <div className={`whitespace-pre-line ${className}`} data-testid={testid}>{html}</div>;
}

export function PolicyModal({ policy, onClose }) {
  if (!policy) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="run-policy-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-[#E9E2D2] run-display text-xl uppercase text-[#08363A]" data-testid="run-policy-title">{policy.title}</div>
        <div className="px-6 py-5 overflow-y-auto text-sm text-[#3a362f] leading-relaxed" data-testid="run-policy-body">
          {policy.html ? <RichHtml html={policy.html} /> : <p className="italic text-[#8a8478]">GS Mandal has not published this policy yet.</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#E9E2D2] flex justify-end">
          <button type="button" onClick={onClose} className="bg-[#0D5C63] hover:bg-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors" data-testid="run-policy-close">Close</button>
        </div>
      </div>
    </div>
  );
}

export const RUN_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&display=swap');
.run-display{font-family:'Barlow Condensed','IBM Plex Sans',sans-serif;font-weight:800;letter-spacing:-0.01em;}
.run-page{font-family:'IBM Plex Sans',sans-serif;}
@keyframes runRise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
.run-rise{animation:runRise .55s cubic-bezier(.2,.7,.2,1) both;}
.run-rise:nth-child(2){animation-delay:.06s}.run-rise:nth-child(3){animation-delay:.12s}.run-rise:nth-child(4){animation-delay:.18s}.run-rise:nth-child(5){animation-delay:.24s}.run-rise:nth-child(6){animation-delay:.3s}
.run-stripe{background-image:repeating-linear-gradient(-45deg,rgba(255,255,255,.06) 0 2px,transparent 2px 14px);}
`;
